import classNames from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import DotsLoading from '../../../components/dotsLoading';
import SecondaryCard from '../../../components/mazraeKit/cards/secondaryCard';
import CustomPaginate from '../../../components/mazraeKit/customPaginate';
import CustomSearchBox from '../../../components/mazraeKit/customSearchBox';
import MainFooter from '../../../components/mazraeKit/footers/footerMain';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import {useGetNearestLabs} from '../../../services/hookService/getNearestLabs/useGetNearestLabs';
import UseGetProfileCity from '../../../services/hookService/getProfileCityName/useGetProfileCity';
import {RootState} from '../../../store/redux';

import {googleAnalyticsInstance} from '../../../utilities/helper/googleAnalitics';
import useDebounce from '../../../utilities/hooks/useDebounce/useDebounce';
import useScrolling from '../../../utilities/hooks/useScrolling/useScrolling';
import LabListingBottomSheet from '../labListingBottomSheet';
import FiltersLabList from './filtersLabList';
import {useLabListingFilters} from '../../../hooks/filters/useLabListingFilter';
import {useGetInputGroupList} from '../../../services/hookService';
import {InputGroupCode} from '../../../utilities/helper/unitGenerator/unit.types';

const PER_PAGE_SIZE = 10;

const LabListing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {labListingFilters, setFilterLabListing} = useLabListingFilters();
  const {inputGroupList} = useGetInputGroupList();
  const labInputGroup = inputGroupList?.find(
    item => item.code === InputGroupCode.LAB,
  );

  const scrollDivElement = useRef<HTMLDivElement | null>(null);
  const {isScrollingDown} = useScrolling(scrollDivElement);

  const [inputValue, setInputValue] = useState<string>(
    labListingFilters?.search || '',
  );
  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedValue = useDebounce(inputValue, 2000);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const {locational} = useSelector(
    (state: RootState) => state.searchingLocation,
  );
  const cities = locational.map(item => item.id);

  const {nearestLabs, nearestLabsIsLoading} = useGetNearestLabs({
    page: currentPage,
    lab_field_code: labListingFilters.input_guid.length
      ? labListingFilters?.input_guid.map(c => c.code)
      : undefined,
    city_id: cities || undefined,
    ordering: labListingFilters.order_by,
    page_size: PER_PAGE_SIZE,
    search: labListingFilters?.search,
  });

  useEffect(() => {
    setFilterLabListing('search', debouncedValue);
  }, [debouncedValue]);

  return (
    <div className="flex relative flex-col w-full h-dvh">
      <div>
        <CustomHeader
          isScrolling={isScrollingDown}
          canScroll={true}
          title="آزمایشگاه ها"
          preIcon="icon-nameArrow---Right"
          isActiveRightIcon
          onClick={() => {
            navigate(-1);
          }}
        />
      </div>
      <div
        className={classNames(
          'overflow-y-hidden px-3 my-3 w-full duration-500 ease-in-out',
          {
            'h-0 opacity-0': isScrollingDown,
            'h-52 opacity-100': !isScrollingDown,
          },
        )}
      >
        <div className="mt-3 mb-6 w-full">
          <CustomSearchBox
            placeholder="جستجو در آزمایشگاه "
            type="text"
            value={inputValue}
            inputCustomClass="!pr-2 !placeholder:text-md !text-md text-gray-700"
            isFloating={false}
            preIcon="icon-nameSearch"
            onChange={onChangeHandler}
            removeIconClassName="!left-20"
            customComponent={() => (
              <div
                className="flex absolute left-0 top-1/2 items-center px-2 py-2 border-r-2 border-gray-100 -translate-y-1/2"
                onClick={() =>
                  navigate('/choose-location', {
                    state: {from: location?.pathname},
                  })
                }
              >
                <i className="text-xl icon-nameLocation" />
                <span
                  className="text-sm"
                  onClick={() =>
                    navigate('/choose-location', {
                      state: {from: location?.pathname},
                    })
                  }
                >
                  {locational?.length
                    ? `${locational?.length} شهرستان`
                    : UseGetProfileCity().profileCity}
                </span>
              </div>
            )}
            {...{inputValue, setInputValue}}
          />
          <div className="flex overflow-x-auto gap-2 items-center mt-3 w-full">
            <button
              className="flex gap-2 items-center px-3 py-2 text-gray-700 rounded-full border w-fit"
              onClick={() => {
                setOpenBottomSheet(true);
                googleAnalyticsInstance.eventGoogleAnalytics({
                  category: 'labFilter',
                  action: 'رو فیلتر آزمایشگاه در لیست آزمایشگاه کلیک شد',
                });
              }}
            >
              <i className="font-bold icon-nameFilter text-xxl" />
              <span className="text-sm whitespace-nowrap">فیلتر کردن</span>
            </button>
            <div>
              <FiltersLabList />
            </div>
          </div>
        </div>
      </div>
      <section
        className="flex overflow-y-auto flex-col gap-4 px-3 h-full"
        ref={scrollDivElement}
      >
        {nearestLabsIsLoading && <DotsLoading />}
        {nearestLabs?.data?.results.map((item: IResults, index: number) => (
          <SecondaryCard
            rate={item.average_rate}
            key={index}
            logo={item.logo}
            title={item.title}
            state={item.state?.name || ''}
            city={item.city?.name || ''}
            cardWrapperClassName="w-full"
            input_groups={item?.lab_field}
            guid={item.guid}
            onClick={() => navigate(`/lab-card-detail/${item.guid}`)}
          />
        ))}
      </section>
      <div
        className={classNames('my-4 duration-500 ease-in-out', {
          'overflow-y-hidden h-0 opacity-0': isScrollingDown,
          'opacity-100': !isScrollingDown,
        })}
      >
        {nearestLabs?.data && (
          <CustomPaginate
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalCount={nearestLabs?.data?.count}
            perPageSize={PER_PAGE_SIZE}
          />
        )}
      </div>
      {labInputGroup && (
        <LabListingBottomSheet
          labInputGroup={labInputGroup}
          openBottomSheet={openBottomSheet}
          setOpenBottomSheet={setOpenBottomSheet}
        />
      )}
      <MainFooter isScrolling={isScrollingDown} />
    </div>
  );
};

export default LabListing;
